import {
  createContext, useContext, useMemo, useRef, useState, type ReactNode,
} from 'react';
import { I18nProvider, type Lang, DEFAULT_LANG } from '../lib/i18n';
import { browserLanguages, resolveLanguage } from '@shared/index';
import { PublicApi, type Catalogue as ApiCatalogue } from '../lib/api';
import { resolveConfig, type SiteConfig } from '../config';
import { installTheme, SCOPE, type Scheme } from '../theme/apply';
import { defaultTheme } from '../theme/default';
import type { Theme } from '../theme/tokens';
import type { Selection } from '../lib/link';
import type { EnquiryTarget } from '../components/EnquiryForm';
import type { ResultsRenderer } from './results';

/**
 * The shell every widget mounts inside, in either host.
 *
 * ---------------------------------------------------------------------------
 * WHY THERE IS A SHELL AT ALL
 * ---------------------------------------------------------------------------
 *
 * The same components run in two places: mounted into a WordPress page by the
 * plugin, and mounted by this app around the enquiry flow. Everything they need
 * that differs between those hosts — the API base, the park, the language, the
 * theme, whether a click leaves the page — arrives here as props and reaches
 * the components through one context. Nothing below this file reads
 * `window.location`, `localStorage` or `document`, which is what keeps the
 * widget mountable anywhere and keeps the two hosts from drifting apart.
 *
 * ---------------------------------------------------------------------------
 * THE SCOPE ELEMENT
 * ---------------------------------------------------------------------------
 *
 * One `div.pmsw`, carrying the theme's tokens and every style the widget uses.
 * `styles.css` explains why that is a class rather than a shadow root. The
 * `data-scheme` attribute on it is what selects light, dark, or follow-the-
 * device — and the default is `light` rather than `system`, because a widget
 * that turns dark inside a park's light page is a rendering bug with a
 * plausible explanation.
 */

export interface WidgetHost {
  api: PublicApi;
  config: SiteConfig;
  /** Injected so a test drives the RDW lookup without touching the network. */
  fetchImpl?: typeof fetch;
  /** Today, as a timestamp. Injectable so a calendar is not a clock-dependent test. */
  now: number;
  /**
   * What happens when a guest picks a stay.
   *
   * Present when the host owns the enquiry — this app. Absent in WordPress,
   * where the widget builds a deep link instead and the guest leaves the page.
   */
  onSelect: (target: EnquiryTarget) => void;
  /**
   * Whether the host actually wants to be told.
   *
   * A separate flag rather than an optional `onSelect`, because `onSelect` is a
   * stable wrapper over a ref — see the comment where it is built — and a
   * wrapper is always truthy. Without this the widget would never fall through
   * to the deep link, which is the WordPress path.
   */
  handlesSelection: boolean;
  /** Where that deep link points. Used when `handlesSelection` is false. */
  enquiryUrl: string;
  /** Break the total down into a price per night. See `WidgetOptions`. */
  showNightlyRate: boolean;
  /**
   * What the widget loaded, handed back to the host.
   *
   * The enquiry form needs the park's country block, and the widget has already
   * fetched it. Reporting it upward costs nothing and saves the app a second
   * identical request for data it is holding one component away.
   */
  onCatalogue?: (catalogue: ApiCatalogue) => void;
}

const HostContext = createContext<WidgetHost | null>(null);

export function useHost(): WidgetHost {
  const host = useContext(HostContext);
  if (!host) throw new Error('widget rendered outside its host');
  return host;
}

export interface WidgetOptions {
  parkId?: string;
  apiBase?: string;
  locale?: Lang;
  /**
   * The host's own language order, tried when `locale` names one we lack.
   *
   * WordPress passes the languages the site publishes. A German page on a site
   * that also publishes Dutch should fall back to Dutch, not to the reader's
   * browser — that is a different question with somebody else's answer.
   */
  locales?: readonly string[];
  /**
   * A language the host controls, with its handler.
   *
   * The plugin does not use these — a WordPress page states its language once
   * and the widget follows. This app does, because a guest who switched on the
   * search screen must not be handed a Dutch enquiry form, and remembering that
   * choice is an application's job rather than a widget's.
   */
  lang?: Lang;
  onLangChange?: (lang: Lang) => void;
  /** Scopes the widget to one accommodation — the variant for its own page. */
  accommodationId?: string;
  /**
   * Scopes the widget to a KIND of accommodation: `camping`, `rental`.
   *
   * The coarse half of `accommodationId`, for a page that is about a part of
   * the park rather than one unit of it. A park's own site nearly always has
   * such pages — "our pitches", "our lodges" — and until now the widget on one
   * of them answered for the whole park, which is how a page selling rental
   * units came to offer the campsite's dog.
   *
   * The kinds are the park's own, published on every card in the catalogue, so
   * this needs no table and no translation.
   */
  kinds?: readonly string[];
  theme?: Theme;
  scheme?: Scheme;
  /** Everything the plugin already knows: dates, party, language. */
  initial?: Selection;
  enquiryUrl?: string;
  siteName?: string;
  currency?: string;
  /** The park's IANA zone, for deciding what "today" is. Empty means UTC. */
  timeZone?: string;
  fetchImpl?: typeof fetch;
  now?: number;
  onSelect?: (target: EnquiryTarget) => void;
  onCatalogue?: (catalogue: ApiCatalogue) => void;
  /**
   * What the answers look like.
   *
   * Defaults to our own tiles. WordPress passes `domResults`, which draws
   * nothing and drives the cards the park's theme already rendered. The seam is
   * explained in `results.ts`; the short version is that search behaviour must
   * be identical in both hosts and tile markup legitimately is not.
   */
  renderResults?: ResultsRenderer;
  /** Off for a host that owns its own date fields and drives us by `initial`. */
  showControls?: boolean;
  /**
   * Whether to offer the attribute filters.
   *
   * On by default, and off for a host with no room for them. The 2027 compat
   * bridge turns them off: it frames a fixed strip on somebody else's page,
   * the park has twenty-four accommodations rather than four hundred, and its
   * camping is an enquiry with no pitch to choose between.
   *
   * Turning them off is a rendering decision and nothing more. Availability is
   * still computed across a SET of types — the set is simply all of them — so
   * this changes what is drawn and not what is answered.
   */
  showFilters?: boolean;
  /** Draw the results section, or leave the answers to the host page. */
  showResults?: boolean;
  /**
   * Break the total down into a price per night. OFF unless asked for.
   *
   * REPORTED, by the operator, with the line circled on a screenshot: do not
   * name a price per night here. A park sells a stay at a price, and a nightly
   * average is not a price anything can be bought at: it moves with the length,
   * it disagrees with every rung on the ladder the park actually publishes, and
   * on a card whose total includes a cleaning fee and a tourist tax it is not
   * even the nightly rate. The guest already has the total and the number of
   * nights; the division is theirs to make if they want it.
   *
   * Kept as an option rather than deleted, because a park whose prices really
   * are per night may well want it, and the WordPress plugin is where that
   * choice belongs.
   */
  showNightlyRate?: boolean;
  /** Paging or scrolling between months. Undefined follows the convention. */
  monthNav?: 'paged' | 'scroll';
  /**
   * Drop the outer border, corners and shadow.
   *
   * For an iframe that a host page places inline with no border of its own:
   * a rounded card inside a square frame draws a second edge a few pixels
   * inside the first, which reads as a rendering fault rather than as a card.
   * The page around it owns the framing.
   */
  flush?: boolean;
}

export function WidgetRoot({ options, children }: {
  options: WidgetOptions;
  children: ReactNode;
}) {
  const config = useMemo(() => resolveConfig({
    apiBase: options.apiBase,
    parkId: options.parkId ?? options.initial?.parkId,
    siteName: options.siteName,
    currency: options.currency,
    appUrl: options.enquiryUrl,
    timeZone: options.timeZone,
  }), [
    options.apiBase, options.parkId, options.initial?.parkId,
    options.siteName, options.currency, options.enquiryUrl, options.timeZone,
  ]);

  const theme = options.theme ?? defaultTheme;
  // During render rather than in an effect: the tokens have to exist before the
  // first paint, or the widget flashes as unstyled text on the host's page.
  // Idempotent, so re-rendering costs a string comparison.
  installTheme(theme);

  // A host that states the language wins; otherwise the browser, and only then
  // the source language. `locale` is what the WordPress plugin passes — the
  // language the PAGE was rendered in — and `initial.lang` is a `?lang=` on an
  // embed. Both are facts about the page rather than preferences, so neither
  // consults the browser.
  const [ownLang, setOwnLang] = useState<Lang>(() => resolveLanguage<Lang>({
    supported: ['en', 'nl'],
    fallback: DEFAULT_LANG,
    host: options.initial?.lang ?? options.locale,
    hostOrder: options.locales,
    browser: browserLanguages(),
  }));
  const lang = options.lang ?? ownLang;
  const setLang = options.onLangChange ?? setOwnLang;

  /**
   * The host's callbacks, held by reference rather than by value.
   *
   * This is not a micro-optimisation, it is a correctness fix with teeth. A
   * caller writes `onSelect: (t) => setTarget(t)` inline — which is the obvious
   * thing to write, and what a WordPress plugin author will write. That is a
   * new function on every render, so a `host` memoised on it is a new `host` on
   * every render, so the API client is new, so the effect that fetches the
   * catalogue reruns, so state changes, so it renders again. An infinite
   * request loop, pointed at a park's server, triggered by ordinary calling
   * code. Holding them in a ref makes the identity of `host` depend only on
   * things that genuinely change.
   */
  const callbacks = useRef<Pick<WidgetOptions, 'onSelect' | 'onCatalogue'>>(options);
  callbacks.current = options;

  // The clock is read once per mount for the same reason. `Date.now()` inside
  // the memo would be a new value every time the memo recomputed, and "today"
  // moving mid-session would rescan the calendar.
  const mountedAt = useRef(options.now ?? Date.now());
  if (options.now !== undefined) mountedAt.current = options.now;

  const handles = options.onSelect !== undefined;

  const host = useMemo<WidgetHost>(() => ({
    api: new PublicApi({
      apiBase: config.apiBase,
      parkId: config.parkId,
      fetch: options.fetchImpl,
    }),
    config,
    fetchImpl: options.fetchImpl,
    now: mountedAt.current,
    onSelect: (target) => callbacks.current.onSelect?.(target),
    onCatalogue: (catalogue) => callbacks.current.onCatalogue?.(catalogue),
    handlesSelection: handles,
    enquiryUrl: config.appUrl,
    showNightlyRate: options.showNightlyRate ?? false,
  }), [config, options.fetchImpl, handles, options.showNightlyRate]);

  return (
    <div
      className={options.flush ? `${SCOPE} pw-flush` : SCOPE}
      data-scheme={options.scheme ?? 'light'}
      data-theme-id={theme.id}
    >
      <I18nProvider lang={lang} setLang={setLang}>
        <HostContext.Provider value={host}>
          {children}
        </HostContext.Provider>
      </I18nProvider>
    </div>
  );
}
